import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtPKR } from "../../lib/api";
import {
  Building2, CheckCircle2, Ban, Users, Wallet, TrendingUp, Briefcase,
  AlertTriangle, ArrowUpRight, Sparkles, Calendar, HandCoins, Coins,
  Timer, ClipboardCheck,
} from "lucide-react";

function Card({ icon: Icon, label, value, hint, tone = "slate", testid, href }) {
  const tones = {
    slate: "bg-slate-800 border-slate-700 text-slate-200",
    amber: "bg-amber-500/10 border-amber-500/30 text-amber-300",
    emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
    red: "bg-red-500/10 border-red-500/30 text-red-300",
    sky: "bg-sky-500/10 border-sky-500/30 text-sky-300",
    violet: "bg-violet-500/10 border-violet-500/30 text-violet-300",
  };
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-md border flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
        {href && <ArrowUpRight className="w-4 h-4 text-slate-500" />}
      </div>
      <div className="mt-4 text-xs text-slate-500 uppercase tracking-wide font-semibold">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold tabular text-white">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </>
  );
  const cls = "bg-[#151A23] border border-slate-800 rounded-md p-5 transition hover:border-slate-700";
  if (href) return <Link to={href} data-testid={testid} className={cls + " block"}>{inner}</Link>;
  return <div data-testid={testid} className={cls}>{inner}</div>;
}

export default function AdminOverview() {
  const [s, setS] = useState(null);
  const [fin, setFin] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data));
    api.get("/admin/reports/financial").then((r) => setFin(r.data)).catch(() => {});
  }, []);

  if (!s) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-overview-loading">
        {["a","b","c","d","e","f","g","h"].map((slot) => (
          <div key={slot} className="h-32 bg-[#151A23] border border-slate-800 rounded-md animate-pulse" />
        ))}
      </div>
    );
  }

  const rev = fin?.revenue || {};
  const cnt = fin?.counts || {};

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <div className="text-[11px] text-amber-400 font-bold uppercase tracking-widest">Business Cockpit</div>
          <h1 className="font-display text-3xl font-bold text-white">Super Admin — Pakistan Overview</h1>
          <p className="text-slate-400 mt-1 text-sm">Revenue split, shops, referrals — real-time control center.</p>
        </div>
        <div className="text-xs text-slate-500">Updated · {new Date().toLocaleString()}</div>
      </div>

      {/* Revenue split — the KPIs Super Admin cares about first */}
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Revenue Split (all-time)</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card icon={Wallet} label="Gross Revenue" value={fmtPKR(rev.gross || 0)} hint={`${cnt.total_subs || 0} subscriptions`} tone="emerald" testid="cockpit-gross" />
          <Card icon={HandCoins} label="Referral Rewards" value={fmtPKR(rev.referral_rewards || 0)} hint={`${cnt.referred_subs || 0} referred subs`} tone="sky" testid="cockpit-ref-rewards" />
          <Card icon={Coins} label="Sub Admin Commission" value={fmtPKR(rev.sub_admin_commissions || 0)} hint="40% of net" tone="violet" testid="cockpit-sub-admin" />
          <Card icon={TrendingUp} label="My Revenue (Super Admin)" value={fmtPKR(rev.super_admin_revenue || 0)} hint="60% of net" tone="amber" testid="cockpit-super-admin" />
        </div>
      </div>

      {/* Subscription breakdown */}
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Subscriptions</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card icon={Calendar} label="Monthly Subscribers" value={cnt.monthly_subs || 0} hint={fmtPKR(rev.monthly || 0)} tone="emerald" testid="cockpit-monthly" />
          <Card icon={Calendar} label="Yearly Subscribers" value={cnt.yearly_subs || 0} hint={fmtPKR(rev.yearly || 0)} tone="sky" testid="cockpit-yearly" />
          <Card icon={ClipboardCheck} label="Verified Payments" value={cnt.verified_payments || 0} tone="emerald" href="/admin/payments" testid="cockpit-verified" />
          <Card icon={Timer} label="Pending Payments" value={cnt.pending_payments || 0} tone={cnt.pending_payments > 0 ? "amber" : "slate"} href="/admin/verify-payments" testid="cockpit-pending" />
        </div>
      </div>

      {/* Shops row */}
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Shops</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card icon={Building2} label="Total Shops" value={s.total_shops} href="/admin/shops" tone="slate" testid="cockpit-shops" />
          <Card icon={CheckCircle2} label="Active" value={s.active_shops} tone="emerald" testid="cockpit-active" />
          <Card icon={Sparkles} label="New (this month)" value={s.new_shops_this_month} tone="emerald" testid="cockpit-new" />
          <Card icon={Ban} label="Suspended" value={s.suspended_shops} tone="red" testid="cockpit-suspended" />
        </div>
      </div>

      {/* Alerts row */}
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Alerts &amp; Partners</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card icon={Calendar} label="Expiring (7d)" value={s.expiring_soon} tone={s.expiring_soon > 0 ? "amber" : "slate"} href="/admin/payments" testid="cockpit-expiring" />
          <Card icon={AlertTriangle} label="Expired" value={s.expired} tone={s.expired > 0 ? "red" : "slate"} href="/admin/payments" testid="cockpit-expired" />
          <Card icon={Users} label="Users" value={s.total_users} hint={`${s.total_customers} customers`} tone="sky" testid="cockpit-users" />
          <Card icon={Briefcase} label="Referral Partners" value={`${s.distributors_active}/${s.distributors_total}`} hint={`${s.distributors_pending} pending`} tone="sky" href="/admin/distributors" testid="cockpit-distributors" />
        </div>
      </div>

      {/* Plans distribution */}
      <div className="bg-[#151A23] border border-slate-800 rounded-md p-6">
        <div className="font-display font-semibold text-lg text-white">Plan distribution</div>
        <div className="text-xs text-slate-500 mb-3">Non-suspended shops</div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(s.shops_by_plan || {}).map(([plan, count]) => (
            <div
              key={plan}
              className="rounded-md border border-slate-700 bg-slate-800 px-4 py-2"
              data-testid={`admin-plan-chip-${plan}`}
            >
              <div className="text-xs text-slate-400 uppercase tracking-wide font-semibold">{plan}</div>
              <div className="font-display text-xl font-bold tabular text-white">{count}</div>
            </div>
          ))}
          {Object.keys(s.shops_by_plan || {}).length === 0 && (
            <div className="text-slate-500 text-sm">No shops yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
